import { useState, useEffect } from "react";
import { Button, Modal, Input, Select, message } from "antd";
import {
  addSoaTable,
  getDpArmList,
} from "src/DigitalProtocolModule/utils/ajax-proxy";
import { SOAFormType } from "./types";
import { ArmAndCohortSelectorPopover } from "..";
import styles from "./index.module.scss";
import "./index.scss";

interface CopySoaTableModalProps {
  visible: boolean;
  tableData: Digital_Protocol_SoA.LandingInfoType[];
  doCancel: () => void;
  doConfirm: () => void;
}

export function CopySoaTableModal({
  visible,
  tableData,
  doCancel,
  doConfirm,
}: CopySoaTableModalProps) {
  const trial_uuid = localStorage.getItem("trial-id");

  const ERROR_MSGS = {
    table_name_req: "Table Name is required.",
    copied_table_id_req: "Copy SOA from previous Table is required.",
  };

  const [isSaving, setIsSaving] = useState(false);
  const [allArms, setAllArms] = useState<Digital_Protocol_Arm_Types.Option[]>(
    []
  );
  const [allCohorts, setAllCohorts] = useState<
    Digital_Protocol_Arm_Types.Option[]
  >([]);
  const [formData, setFormData] = useState<SOAFormType>({
    table_name: "",
    copied_table_id: null,
    arm_id: [],
    cohort_id: [],
  });
  const [error, setError] = useState({
    table_name: "",
    copied_table_id: "",
    arm_id: "",
    cohort_id: "",
  });

  const handleChange = (val: any, field: string) => {
    setFormData({
      ...formData,
      [field]: val,
    });
  };

  const doValidation = () => {
    let isValid = true;
    let tmpError = { ...error };
    if (!Boolean(formData.table_name.trim())) {
      tmpError.table_name = ERROR_MSGS.table_name_req;
      isValid = false;
    } else {
      tmpError.table_name = "";
    }

    if (!Boolean(formData.copied_table_id)) {
      tmpError.copied_table_id = ERROR_MSGS.copied_table_id_req;
      isValid = false;
    } else {
      tmpError.copied_table_id = "";
    }

    setError(tmpError);
    return isValid;
  };

  const handleSubmit = async () => {
    const isValid = doValidation();
    if (!isValid) {
      return;
    }

    try {
      // get dprot id
      const tableItem = tableData.find(
        (item) => item.table_id === formData.copied_table_id
      );
      setIsSaving(true);
      const res = await addSoaTable({
        table_name: formData.table_name.trim(),
        copied_table_id: formData.copied_table_id,
        dprot_id: tableItem?.dprot_id,
        table_counter_id: tableItem?.table_counter_id,
        arm_id: formData.arm_id.map((item) => item.id),
        cohort_id: formData.cohort_id.map((item) => item.id),
        trial_uuid,
      });
      if (res?.error?.includes("table name is duplicate")) {
        message.error(
          "SOA table name already exists. Please enter a unique name"
        );
      } else if (res?.error?.includes("cohort id is duplicate")) {
        message.error("One cohort cannot be linked to multiple SOA tables");
      } else if (res?.table_id) {
        message.success("Save successfully");
        // close the modal and fetch table list
        doConfirm();
      } else {
        message.error("Failed to save");
      }
    } catch (error) {
      message.error("Failed to save");
    } finally {
      setIsSaving(false);
    }
  };

  const fetchArmsList = async () => {
    getDpArmList(trial_uuid)
      .then((res: Digital_Protocol_Arm_Types.Arm[]) => {
        setAllArms(
          res
            .filter((item: Digital_Protocol_Arm_Types.Arm) => !item.arm_mapped)
            .map((item: Digital_Protocol_Arm_Types.Arm) => ({
              id: item.arm_id,
              name: item.arm_title,
              description: item.arm_description,
              subOptions: item.cohorts,
              subOptionIds: item.cohorts.map((subItem) => subItem.cohort_id),
            }))
        );
      })
      .catch((e) => {
        message.error("Failed to fetch the Arms");
      })
      .finally(() => {});
  };

  useEffect(() => {
    if (formData.arm_id.length === 0) {
      setAllCohorts([]);
      handleChange([], "cohort_id");
    } else {
      const arm_id = formData.arm_id.map((item) => item.id);
      const cohortsInScope: Digital_Protocol_Arm_Types.Option[] = allArms
        .filter((item) => arm_id.includes(item.id))
        .reduce(
          (accumulator, currentValue) => [
            ...accumulator,
            ...currentValue.subOptions,
          ],
          []
        )
        .map((item) => ({
          id: item.cohort_id,
          name: item.cohort_name,
          description: item.cohort_description,
        }));
      setAllCohorts(cohortsInScope);
    }
  }, [formData.arm_id]);

  useEffect(() => {
    if (visible) {
      fetchArmsList();
    } else {
      setFormData({
        table_name: "",
        copied_table_id: null,
        arm_id: [],
        cohort_id: [],
      });
      setError({
        table_name: "",
        copied_table_id: "",
        arm_id: "",
        cohort_id: "",
      });
    }
  }, [visible]);

  return (
    <Modal
      visible={visible}
      title="Create Schedule of Activity"
      onCancel={doCancel}
      footer={[
        <Button key="back" onClick={doCancel}>
          Cancel
        </Button>,
        <Button
          key="submit"
          style={{
            backgroundColor: "#ca4a04",
            color: "#fff",
            borderColor: "#ca4a04",
          }}
          onClick={handleSubmit}
          loading={isSaving}
        >
          Submit
        </Button>,
      ]}
      wrapClassName="confirm-modal-root"
    >
      <div className={`${styles.body} cp-soa-table-modal`}>
        <div>
          <label>
            Table Name <span className={styles.required}>*</span>
          </label>
          <Input
            value={formData.table_name}
            onChange={(e) => handleChange(e.target.value, "table_name")}
            onBlur={(e) => {
              if (!Boolean(e.target.value.trim())) {
                setError({
                  ...error,
                  table_name: ERROR_MSGS.table_name_req,
                });
              } else {
                setError({
                  ...error,
                  table_name: "",
                });
              }
            }}
            maxLength={50}
          />
          <span className={styles.errorMsg}>{error.table_name}</span>
        </div>

        <div>
          <label>
            Copy SOA from previous Table{" "}
            <span className={styles.required}>*</span>
          </label>
          <div>
            <Select
              value={formData.copied_table_id}
              onChange={(val) => handleChange(val, "copied_table_id")}
              onBlur={() => {
                if (!Boolean(formData.copied_table_id)) {
                  setError({
                    ...error,
                    copied_table_id: ERROR_MSGS.copied_table_id_req,
                  });
                } else {
                  setError({
                    ...error,
                    copied_table_id: "",
                  });
                }
              }}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option: any) =>
                (option?.label ?? "").includes(input)
              }
              filterSort={(optionA: any, optionB: any) =>
                (optionA?.label ?? "")
                  .toLowerCase()
                  .localeCompare((optionB?.label ?? "").toLowerCase())
              }
              options={tableData.map(({ table_id, table_name }) => ({
                value: table_id,
                label: table_name,
              }))}
            />
          </div>
          <span className={styles.errorMsg}>{error.copied_table_id}</span>
        </div>

        <div>
          <label>Map Arm</label>
          <div className={styles.inputBox}>
            {formData.arm_id.map((item) => (
              <div className={styles.cohortTag} key={item.id}>
                {item.name}
              </div>
            ))}
            <ArmAndCohortSelectorPopover
              category="Arm"
              allOptions={allArms}
              selected={{
                arms: (formData?.arm_id || []).map((item) => ({
                  id: item.id,
                  name: item.name,
                })),
                cohorts: (formData.cohort_id || []).map((item) => ({
                  id: item.id,
                  name: item.name,
                })),
              }}
              doConfirm={(data) => {
                handleChange(data, "arm_id");
              }}
            />
          </div>
        </div>

        <div>
          <label>Map Cohort</label>
          <div className={styles.inputBox}>
            {formData.cohort_id.map((item) => (
              <div className={styles.cohortTag} key={item.id}>
                {item.name}
              </div>
            ))}
            <ArmAndCohortSelectorPopover
              category="Cohort"
              allOptions={allCohorts}
              selected={{
                arms: (formData?.arm_id || []).map((item) => ({
                  id: item.id,
                  name: item.name,
                })),
                cohorts: (formData.cohort_id || []).map((item) => ({
                  id: item.id,
                  name: item.name,
                })),
              }}
              errMsg={
                formData?.arm_id?.length > 0 ? "" : "Please select Arm first."
              }
              doConfirm={(data) => {
                handleChange(data, "cohort_id");
              }}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
}
